/**
 * Resident todos data hook — the seam every planner surface consumes (spec
 * 2026-10-02 §7.1). The slice holds the stored records; this hook turns them
 * into what a surface shows: the items in ITS range (`from`–`to`, so repeats
 * expand over exactly the visible days), the Overdue list over the whole
 * history, and save/remove/toggle helpers that take the item itself — a
 * repeating date needs its series and its SCHEDULED date.
 */
import { useCallback, useMemo } from 'react';
import type {
  EditScope,
  ResidentTodo,
  ResidentTodoRecord,
  TodoFormValues,
} from '@/types/residents/todos';
import {
  useAddResidentTodoMutation,
  useDeleteResidentTodoMutation,
  useGetResidentTodosQuery,
  useUpdateResidentTodoMutation,
} from '@/store/api/residentTodosApi';
import { expandTodos, overdueTodos as computeOverdue } from '@/utils/residents/todoRecurrence';
import { buildCreateInput, buildUpdateInput } from '@/utils/residents/todoScope';

export interface UseResidentTodosArgs {
  /** First visible day, 'YYYY-MM-DD'. */
  from: string;
  /** Last visible day, inclusive. */
  to: string;
  /** Today: the Overdue cutoff. */
  todayKey: string;
}

export interface UseResidentTodosResult {
  /** Items shown in [from, to]. */
  todos: ResidentTodo[];
  /** Overdue rows: past one-offs + at most one row per repeat. */
  overdueTodos: ResidentTodo[];
  isLoading: boolean;
  isError: boolean;
  /** A response has landed (even an empty one): errors with cached data keep the list. */
  hasData: boolean;
  refetch: () => void;
  toggleTodo: (todo: ResidentTodo) => void;
  /** `todo` null = create. `scope` only for a repeating date. */
  saveTodo: (todo: ResidentTodo | null, values: TodoFormValues, scope?: EditScope) => Promise<unknown>;
  removeTodo: (todo: ResidentTodo, scope?: EditScope) => Promise<void>;
}

const EMPTY_RECORDS: ResidentTodoRecord[] = [];
const EMPTY_TODOS: ResidentTodo[] = [];

export function useResidentTodos({ from, to, todayKey }: UseResidentTodosArgs): UseResidentTodosResult {
  const { data, isLoading, isError, refetch } = useGetResidentTodosQuery();
  const [addMutation] = useAddResidentTodoMutation();
  const [updateMutation] = useUpdateResidentTodoMutation();
  const [deleteMutation] = useDeleteResidentTodoMutation();

  const records = data ?? EMPTY_RECORDS;
  const todos = useMemo(
    () => (records.length === 0 ? EMPTY_TODOS : expandTodos(records, from, to)),
    [records, from, to],
  );
  const overdueTodos = useMemo(
    () => (records.length === 0 ? EMPTY_TODOS : computeOverdue(records, todayKey)),
    [records, todayKey],
  );

  // Fire-and-forget on purpose: the slice's optimistic patch flips the row at
  // once and rolls back on error. The CURRENT state is read from the cached
  // record, never from the item handed in — that may be a stale snapshot (an
  // Overdue row on its way out) or outside this range.
  const toggleTodo = useCallback(
    (todo: ResidentTodo) => {
      if (todo.seriesId && todo.occurrenceDate) {
        const record = records.find(r => r.id === todo.seriesId);
        if (!record) return;
        const current =
          record.occurrences.find(o => o.date === todo.occurrenceDate)?.completed ?? false;
        void updateMutation({
          id: todo.seriesId,
          scope: 'single',
          occurrenceDate: todo.occurrenceDate,
          completed: !current,
        });
        return;
      }
      const record = records.find(r => r.id === todo.id);
      if (!record) return;
      void updateMutation({ id: todo.id, completed: !record.completed });
    },
    [records, updateMutation],
  );

  const saveTodo = useCallback(
    (todo: ResidentTodo | null, values: TodoFormValues, scope?: EditScope) =>
      todo
        ? updateMutation(buildUpdateInput(todo, values, scope)).unwrap()
        : addMutation(buildCreateInput(values)).unwrap(),
    [addMutation, updateMutation],
  );

  const removeTodo = useCallback(
    async (todo: ResidentTodo, scope?: EditScope) => {
      if (todo.seriesId && todo.occurrenceDate) {
        await deleteMutation({
          id: todo.seriesId,
          scope: scope ?? 'all',
          occurrenceDate: todo.occurrenceDate,
        }).unwrap();
        return;
      }
      await deleteMutation({ id: todo.id }).unwrap();
    },
    [deleteMutation],
  );

  return {
    todos,
    overdueTodos,
    isLoading,
    isError,
    hasData: data !== undefined,
    refetch,
    toggleTodo,
    saveTodo,
    removeTodo,
  };
}
